import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ElementRef, Component, OnInit, Input,
  OnChanges, SimpleChanges, Output, EventEmitter,
  ViewChild, AfterViewInit
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDatepickerInput } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { fromEvent } from 'rxjs';
import { NgxMaskService } from 'ngx-mask';
import moment, { Moment } from 'moment';

interface ParseDateValueData {
  isValid: boolean;
  parsedMoment?: Moment;
}

@UntilDestroy()
@Component({
    selector: 'ifc-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss'],
    standalone: false
})
export class DatepickerComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild(MatInput, { static: true }) formFieldControl: MatInput;
  @ViewChild(MatDatepickerInput, { static: true }) datepickerInput: MatDatepickerInput<Date>;
  @ViewChild('dateInput', { static: true }) dateInput: ElementRef;

  @Input()
  fc: UntypedFormControl;

  @Input()
  label: string;

  @Input()
  disabled: boolean;

  @Input()
  min: Date = new Date(1700, 0);

  @Input()
  max: Date = new Date(2300, 0);

  @Input()
  clearDisabled: boolean;

  @Output()
  blured = new EventEmitter<void>();

  @Output()
  clearInput = new EventEmitter<void>();

  labelId: string;
  private previousDate: Date;
  private timeAdjusting = false;
  private maskPattern: string = '00/00/0000';

  constructor(private readonly maskService: NgxMaskService) { }

  ngOnInit(): void {
    this.initPreviousDate(this.fc.value);

    this.fc.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.handleDateValueChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) {
      this.disabled ? this.fc.disable() : this.fc.enable();
    }
    if (changes.label) {
      this.labelId = this.label.toLowerCase().replace(/ /g, '-');
    }
  }

  ngAfterViewInit(): void {
    this.applyMask();
    // Material 20+ reads the value from event.target, so re-parse the masked text via the event.
    fromEvent<Event>(this.dateInput.nativeElement, 'input')
      .pipe(untilDestroyed(this))
      .subscribe(event => {
        this.applyMask();
        this.datepickerInput._onInput(event);
      });
  }

  private initPreviousDate(value: unknown): void {
    if (value instanceof Date) {
      this.previousDate = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const m = moment(value);
      if (m.isValid()) {
        this.previousDate = m.toDate();
      }
    }
  }

  private handleDateValueChange(value: unknown): void {
    const parseDateValueData = this.parseDateValue(value)
    if (this.timeAdjusting || !parseDateValueData.isValid) {
      return;
    }

    const newMoment = parseDateValueData.parsedMoment;
    if (!this.previousDate) {
      this.previousDate = newMoment.toDate();
      return;
    }

    const prevMoment = moment(this.previousDate);
    if (newMoment.isSame(prevMoment, 'day')) {
      this.previousDate = newMoment.toDate();
      return;
    }

    this.timeAdjusting = true;
    const merged = newMoment
      .clone()
      .hour(prevMoment.hour())
      .minute(prevMoment.minute())
      .second(prevMoment.second())
      .toDate();
    // Model only: writing back to the input would reformat a half-typed date ("09/01/2" -> "09/01/0002").
    this.fc.setValue(merged, { emitModelToViewChange: false });
    this.previousDate = merged;
    this.timeAdjusting = false;
  }

  private parseDateValue(value: unknown): ParseDateValueData {
    if (!moment.isMoment(value) && !(value instanceof Date) && typeof value !== 'string' && typeof value !== 'number') {
      return { isValid: false };
    }

    if (moment.isMoment(value)) {
      return {
        isValid: (value as Moment).isValid(),
        parsedMoment: (value as Moment).clone()
      };
    }

    const parsedMoment = moment(value);
    return {
      isValid: parsedMoment.isValid(),
      parsedMoment
    };
  }

  private applyMask(): void {
    this.dateInput.nativeElement.value = this.maskService.applyMask(this.dateInput.nativeElement.value, this.maskPattern);
  }

  onBlur(): void {
    if (this.fc.value === null) {
      this.fc.setValue('');
    }
    this.blured.emit();
  }

  valueClear(): void {
    this.fc.setValue(null);
    this.clearInput.emit();
    this.blured.emit();
  }

  onClose(): void {
    this.blured.emit();
  }
}
